import { useEffect, useState } from 'react';
import { staffApi, formatApiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { PlusCircle, Archive, ArchiveRestore } from 'lucide-react';

export function PositionsDialog({ open, onOpenChange, onChanged }) {
  const [positions, setPositions] = useState([]);
  const [name, setName] = useState('');
  const [color, setColor] = useState('#E8833A');
  const [department, setDepartment] = useState('');
  const [busy, setBusy] = useState(false);

  const load = () => staffApi.get('/scheduling/positions?includeArchived=true').then(({ data }) => setPositions(data.positions));
  useEffect(() => { if (open) load(); }, [open]);

  const add = async () => {
    if (!name.trim()) return toast.error('Enter a position name');
    setBusy(true);
    try {
      await staffApi.post('/scheduling/positions', { name, color, department });
      setName(''); setDepartment('');
      load(); onChanged?.();
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };

  const toggleArchive = async (p) => {
    try {
      await staffApi.patch(`/scheduling/positions/${p.id}`, { isArchived: !p.isArchived });
      load(); onChanged?.();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="positions-dialog" className="max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Roles / Positions</DialogTitle></DialogHeader>
        <div className="flex items-end gap-2">
          <div className="flex-1 space-y-1.5">
            <Label>Name</Label>
            <Input data-testid="position-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Cashier" />
          </div>
          <div className="space-y-1.5">
            <Label>Color</Label>
            <input type="color" data-testid="position-color-input" value={color} onChange={(e) => setColor(e.target.value)} className="h-10 w-12 rounded border border-border/40" />
          </div>
          <Button data-testid="position-add-btn" disabled={busy} onClick={add} className="bg-saffron hover:bg-saffron-hover gap-1.5"><PlusCircle size={15} /> Add</Button>
        </div>
        <div className="space-y-1.5">
          <Input data-testid="position-dept-input" value={department} onChange={(e) => setDepartment(e.target.value)} placeholder="Department (optional)" />
        </div>
        <div className="divide-y divide-border/30 mt-2">
          {positions.map((p) => (
            <div key={p.id} className="py-2.5 flex items-center justify-between" data-testid="position-row">
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full" style={{ background: p.color || '#ccc' }} />
                <span className={`text-sm ${p.isArchived ? 'text-muted-ink line-through' : 'text-charcoal'}`}>{p.name}</span>
                {p.department && <span className="text-xs text-muted-ink">· {p.department}</span>}
              </div>
              <Button variant="ghost" size="sm" data-testid="position-archive-btn" onClick={() => toggleArchive(p)} className="gap-1.5 text-muted-ink">
                {p.isArchived ? <><ArchiveRestore size={14} /> Restore</> : <><Archive size={14} /> Archive</>}
              </Button>
            </div>
          ))}
          {positions.length === 0 && <p className="text-sm text-muted-ink py-2">No positions yet.</p>}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function StaffRolesDialog({ open, onOpenChange, staffMember, positions, showWages, onSaved }) {
  const [selected, setSelected] = useState([]);
  const [primary, setPrimary] = useState('');
  const [wage, setWage] = useState('');
  const [busy, setBusy] = useState(false);
  const [avail, setAvail] = useState([]);
  const [aDay, setADay] = useState('1');
  const [aType, setAType] = useState('UNAVAILABLE');

  const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const loadAvail = () => staffMember && staffApi.get(`/scheduling/staff/${staffMember.id}/availability`).then(({ data }) => setAvail(data.availability));

  useEffect(() => {
    if (open && staffMember) {
      setSelected(staffMember.positions.map((p) => p.id));
      setPrimary(staffMember.positions.find((p) => p.isPrimary)?.id || '');
      setWage(staffMember.hourlyWage ?? '');
      loadAvail();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, staffMember]);

  if (!staffMember) return null;

  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const addAvail = async () => {
    try {
      await staffApi.post(`/scheduling/staff/${staffMember.id}/availability`, { dayOfWeek: Number(aDay), type: aType });
      loadAvail();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const delAvail = async (id) => {
    try { await staffApi.delete(`/scheduling/availability/${id}`); loadAvail(); } catch (err) { toast.error(formatApiError(err)); }
  };

  const save = async () => {
    setBusy(true);
    try {
      await staffApi.put(`/scheduling/staff/${staffMember.id}/positions`, {
        positionIds: selected,
        primaryPositionId: primary || null,
        ...(showWages ? { hourlyWage: wage } : {}),
      });
      toast.success('Saved');
      onSaved?.();
      onOpenChange(false);
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="staff-roles-dialog" className="max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{staffMember.name} — Roles &amp; Wage</DialogTitle></DialogHeader>
        <div className="space-y-2">
          <Label>Qualified roles</Label>
          {positions.map((p) => (
            <label key={p.id} className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" data-testid="staff-role-checkbox" checked={selected.includes(p.id)} onChange={() => toggle(p.id)} className="h-4 w-4 accent-saffron" />
              <span className="w-3 h-3 rounded-full" style={{ background: p.color || '#ccc' }} />
              {p.name}
            </label>
          ))}
        </div>
        <div className="space-y-1.5">
          <Label>Primary role</Label>
          <Select value={primary || 'none'} onValueChange={(v) => setPrimary(v === 'none' ? '' : v)}>
            <SelectTrigger data-testid="staff-primary-role-select"><SelectValue placeholder="None" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">— None —</SelectItem>
              {positions.filter((p) => selected.includes(p.id)).map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {showWages && (
          <div className="space-y-1.5">
            <Label>Hourly wage ($)</Label>
            <Input type="number" min="0" step="0.01" data-testid="staff-wage-input" value={wage} onChange={(e) => setWage(e.target.value)} placeholder="e.g. 18.50" />
          </div>
        )}

        <div className="pt-3 border-t border-border/30 space-y-2">
          <Label>Availability (admin override)</Label>
          <div className="space-y-1">
            {avail.map((a) => (
              <div key={a.id} className="flex items-center justify-between text-sm" data-testid="admin-availability-row">
                <span className={a.type === 'UNAVAILABLE' ? 'text-red-600' : 'text-charcoal'}>
                  {DAYS[a.dayOfWeek]} · {a.type}{a.startMinute != null ? ` ${Math.floor(a.startMinute / 60)}:00–${Math.floor(a.endMinute / 60)}:00` : ' (all day)'}
                </span>
                <button data-testid="admin-availability-delete" onClick={() => delAvail(a.id)} className="text-destructive text-xs">Remove</button>
              </div>
            ))}
            {avail.length === 0 && <p className="text-xs text-muted-ink">No availability set.</p>}
          </div>
          <div className="flex items-center gap-2">
            <Select value={aDay} onValueChange={setADay}>
              <SelectTrigger data-testid="avail-day-select" className="w-28"><SelectValue /></SelectTrigger>
              <SelectContent>{DAYS.map((d, i) => <SelectItem key={i} value={String(i)}>{d}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={aType} onValueChange={setAType}>
              <SelectTrigger data-testid="avail-type-select" className="flex-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="AVAILABLE">Available</SelectItem>
                <SelectItem value="PREFERRED">Preferred</SelectItem>
                <SelectItem value="UNAVAILABLE">Unavailable</SelectItem>
              </SelectContent>
            </Select>
            <Button size="sm" data-testid="avail-add-btn" onClick={addAvail} className="bg-saffron hover:bg-saffron-hover">Add</Button>
          </div>
        </div>

        <DialogFooter>
          <Button data-testid="staff-roles-save-btn" disabled={busy} onClick={save} className="bg-saffron hover:bg-saffron-hover">{busy ? 'Saving…' : 'Save'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const NUM_FIELDS = [
  ['defaultShiftMinutes', 'Default shift length (min)'],
  ['defaultBreakMinutes', 'Default break (min)'],
  ['maxDailyHours', 'Max daily hours'],
  ['maxWeeklyHours', 'Max weekly hours'],
  ['overtimeThreshold', 'Overtime threshold (h)'],
  ['minRestHours', 'Min rest between shifts (h)'],
];
const TOGGLES = [
  ['allowManagerOverride', 'Allow managers to override conflict warnings'],
  ['allowOverlap', 'Allow overlapping shifts'],
  ['requireConfirmation', 'Require employees to confirm shifts'],
  ['notifyOnPublish', 'Notify employees when schedule is published'],
  ['allowAvailability', 'Employees can set availability'],
  ['allowTimeOff', 'Employees can request time off'],
  ['allowShiftSwap', 'Employees can request shift swaps'],
  ['allowReplacement', 'Employees can request replacements'],
  ['allowOpenShiftClaim', 'Employees can claim open shifts'],
  ['requireSwapApproval', 'Shift swaps require manager approval'],
  ['requireClaimApproval', 'Open-shift claims require manager approval'],
];

export function SettingsDialog({ open, onOpenChange, isAdmin, onSaved }) {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) staffApi.get('/scheduling/settings').then(({ data }) => setS(data.settings)); }, [open]);
  if (!s) return null;

  const save = async () => {
    setBusy(true);
    try {
      await staffApi.patch('/scheduling/settings', s);
      toast.success('Settings saved');
      onSaved?.();
      onOpenChange(false);
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="scheduling-settings-dialog" className="max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Scheduling Settings</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Week starts on</Label>
              <Select value={String(s.weekStartsOn)} onValueChange={(v) => setS({ ...s, weekStartsOn: Number(v) })}>
                <SelectTrigger data-testid="setting-weekstart-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">Monday</SelectItem>
                  <SelectItem value="0">Sunday</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Time format</Label>
              <Select value={s.timeFormat} onValueChange={(v) => setS({ ...s, timeFormat: v })}>
                <SelectTrigger data-testid="setting-timeformat-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="12h">12-hour</SelectItem>
                  <SelectItem value="24h">24-hour</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {NUM_FIELDS.map(([k, label]) => (
              <div key={k} className="space-y-1.5">
                <Label>{label}</Label>
                <Input type="number" data-testid={`setting-${k}`} value={s[k]} disabled={!isAdmin} onChange={(e) => setS({ ...s, [k]: Number(e.target.value) })} />
              </div>
            ))}
          </div>
          <div className="pt-2 border-t border-border/30 space-y-3">
            <p className="text-xs font-medium text-muted-ink uppercase tracking-wide">Employee permissions &amp; rules</p>
            {TOGGLES.map(([k, label]) => (
              <label key={k} className="flex items-center justify-between text-sm">
                <span className="text-charcoal pr-3">{label}</span>
                <Switch data-testid={`setting-${k}`} checked={s[k]} disabled={!isAdmin} onCheckedChange={(v) => setS({ ...s, [k]: v })} />
              </label>
            ))}
          </div>
        </div>
        <DialogFooter>
          {isAdmin ? (
            <Button data-testid="settings-save-btn" disabled={busy} onClick={save} className="bg-saffron hover:bg-saffron-hover">{busy ? 'Saving…' : 'Save Settings'}</Button>
          ) : (
            <p className="text-xs text-muted-ink">Only an Admin can change scheduling settings.</p>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
